import { fcfa, moisCourt, nombre } from '../api/format'
import { repartitionPaliers } from '../api/mrr'
import { LIBELLE_PALIER } from '../api/abonnement'
import type { DonneesPlateforme, PalierCode } from '../api/types'

/** Revenus d'abonnement encaissés par mois (12 derniers mois). */
export function Revenus({ revenus }: { revenus: DonneesPlateforme['revenus'] }) {
  const total = revenus.reduce((s, r) => s + r.amount, 0)
  const lignes = revenus.slice(-12)
  const max = Math.max(1, ...lignes.map((r) => r.amount))
  return (
    <section className="carte">
      <div className="carte-tete">
        <div>
          <h2>Revenus encaissés par mois</h2>
          <p className="sous">
            {revenus.length
              ? `${revenus.length} mois · ${fcfa(total)} au total`
              : 'en attente des premiers règlements'}
          </p>
        </div>
      </div>
      <div className="carte-corps">
        {revenus.length === 0 ? (
          <div className="vide">
            Aucun paiement d'abonnement encaissé pour l'instant.
            <br />
            <span>Le graphique se remplira dès les premiers règlements, ou dès qu'une activation manuelle sera suivie d'un encaissement enregistré.</span>
          </div>
        ) : (
          <>
            <div
              className="barres" role="img"
              aria-label={`Revenus des ${lignes.length} derniers mois : ${lignes.map((r) => `${moisCourt(r.month)} ${fcfa(r.amount)}`).join(', ')}`}
            >
              {lignes.map((r) => (
                <div className="barre" key={r.month} title={`${r.month} — ${fcfa(r.amount)} (${r.payments} paiement(s))`}>
                  <span className="val">{nombre(r.amount / 1000)}k</span>
                  <div className="col" style={{ height: `${Math.max(4, Math.round((r.amount / max) * 128))}px` }} />
                </div>
              ))}
            </div>
            <div className="axe" aria-hidden="true">
              {lignes.map((r) => <span key={r.month}>{moisCourt(r.month)}</span>)}
            </div>
          </>
        )}
      </div>
    </section>
  )
}

const COULEURS: Record<PalierCode, string> = { starter: '#0F8A6A', pro: '#2F5FC4', business: '#5B4BD1' }

/** Tenants par palier EFFECTIF (celui que la base applique, pas le palier facturé). */
export function Repartition({ donnees }: { donnees: DonneesPlateforme }) {
  const nb = repartitionPaliers(donnees.organisations, donnees.chargeLe)
  const total = nb.starter + nb.pro + nb.business
  return (
    <section className="carte">
      <div className="carte-tete">
        <div>
          <h2>Répartition par palier</h2>
          <p className="sous">Palier effectivement appliqué à chaque tenant</p>
        </div>
      </div>
      <div className="carte-corps">
        {total === 0 ? (
          <div className="vide">Aucun tenant enregistré.</div>
        ) : (
          (['starter', 'pro', 'business'] as const).map((p) => {
            const pct = Math.round((nb[p] / total) * 100)
            return (
              <div className="rang" key={p}>
                <span className="pastille" style={{ background: COULEURS[p] }} aria-hidden="true" />
                <span className="rang-nom">{LIBELLE_PALIER[p]}</span>
                <div className="jauge" role="img" aria-label={`${LIBELLE_PALIER[p]} : ${nb[p]} tenant(s), ${pct} %`}>
                  <div style={{ width: `${pct}%`, background: COULEURS[p] }} />
                </div>
                <b className="rang-n">{nb[p]} <span>({pct} %)</span></b>
              </div>
            )
          })
        )}
      </div>
    </section>
  )
}
